import { StorySlug } from "@/api/episodes-types";
import { type CreateMasterCombination } from "@/api/master-combinations-types";
import { Prompt, PromptType } from "@/api/prompts-types";
import { StoryConfig } from "@/api/story-configs-types";
import { ModelOption } from "@/components/story-generation/types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { useCreateMasterCombination } from "@/queries/master-combinations/use-create-master-combinations";
import { useAllPrompts } from "@/queries/prompts/use-prompts";
import { useStoryConfigs } from "@/queries/story-configs/use-story-configs";
import { prettyStoryType } from "@/utils/pretty";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useForm, useStore } from "@tanstack/react-form";
import { useState } from "react";

export function CreateMasterCombination() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <CreateMasterCombinationButton onClick={() => setOpen(true)} />
      <CreateMasterCombinationDialog
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export function CreateMasterCombinationButton({
  onClick,
}: {
  onClick: () => void;
}) {
  return (
    <Button variant={"contained"} onClick={onClick}>
      New master combination
    </Button>
  );
}

function CreateMasterCombinationDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    mutate: createMasterCombination,
    isPending,
    error,
    reset: resetCreateMasterCombination,
  } = useCreateMasterCombination();

  const { data: storyConfigs } = useStoryConfigs();
  const { data: prompts } = useAllPrompts();

  function getStoryConfigOptions(selectedStoryType: StorySlug | undefined) {
    if (!selectedStoryType || !storyConfigs) {
      return [];
    }
    const revisions =
      storyConfigs.items.find(
        (config) => config.storyType === selectedStoryType,
      )?.revisions || [];

    return revisions
      .map((revision) => revision.revision)
      .toSorted((a, b) => a - b);
  }

  function getPromptNameOptions(
    selectedStoryType: StorySlug | undefined,
    promptType: PromptType,
  ) {
    if (!selectedStoryType || !prompts) {
      return [];
    }
    return prompts.items
      .filter(
        (prompt) =>
          prompt.storyType === selectedStoryType && prompt.type === promptType,
      )
      .map((prompt) => prompt.name);
  }

  function getPromptRevisionOptions(
    selectedStoryType: StorySlug | undefined,
    selectedPromptName: string | undefined,
    promptType: PromptType,
  ) {
    if (!selectedPromptName || !selectedStoryType || !prompts) {
      return [];
    }
    const prompt = prompts.items.find(
      (p) =>
        p.name === selectedPromptName &&
        p.storyType === selectedStoryType &&
        p.type === promptType,
    );
    if (!prompt) {
      return [];
    }
    return prompt.revisions
      .map((revision) => revision.revision)
      .toSorted((a, b) => a - b);
  }

  const form = useForm({
    defaultValues: {
      name: "",
      storyType: undefined,
      storyConfigRevisionNumber: undefined,
      promptName: undefined,
      promptRevisionNumber: undefined,
      imagePromptName: undefined,
      imagePromptRevisionNumber: undefined,
      llmModel: undefined,
    } as FormValues,
    onSubmit: ({ value, formApi }) => {
      if (formApi.state.isDefaultValue) {
        handleClose();
        return;
      }
      const data = fromFormValues(
        value,
        storyConfigs?.items || [],
        prompts?.items || [],
      );
      createMasterCombination(data, {
        onSuccess: () => {
          handleClose();
        },
      });
    },
  });

  const selectedStoryType = useStore(
    form.store,
    (state) => state.values.storyType,
  );
  const selectedPromptName = useStore(
    form.store,
    (state) => state.values.promptName,
  );
  const selectedImagePromptName = useStore(
    form.store,
    (state) => state.values.imagePromptName,
  );

  const storyConfigRevisionOptions = getStoryConfigOptions(selectedStoryType);
  const promptNameOptions = getPromptNameOptions(
    selectedStoryType,
    PromptType.text,
  );
  const promptRevisionOptions = getPromptRevisionOptions(
    selectedStoryType,
    selectedPromptName,
    PromptType.text,
  );

  const imagePromptNameOptions = getPromptNameOptions(
    selectedStoryType,
    PromptType.image,
  );
  const imagePromptRevisionOptions = getPromptRevisionOptions(
    selectedStoryType,
    selectedImagePromptName,
    PromptType.image,
  );

  const handleClose = () => {
    form.reset();
    resetCreateMasterCombination();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form>
        <DialogTitle>Create New Master Combination</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={error ? 0 : 0.75}>
            {error && <ApiErrorAlert error={error} />}
            <form.Field
              name="name"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) {
                    return "Name is required";
                  }
                },
              }}
              children={(field) => (
                <TextField
                  label="Name"
                  value={field.state.value}
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            />
            <form.Field
              name="storyType"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) {
                    return "Story type is required";
                  }
                },
              }}
              listeners={{
                onChange: ({ fieldApi }) => {
                  fieldApi.form.resetField("storyConfigRevisionNumber");
                  fieldApi.form.resetField("promptName");
                  fieldApi.form.resetField("promptRevisionNumber");
                  fieldApi.form.resetField("imagePromptName");
                  fieldApi.form.resetField("imagePromptRevisionNumber");
                },
              }}
              children={(field) => (
                <TextField
                  label="Story type"
                  value={field.state.value ?? ""}
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  select={true}
                  onChange={(e) => {
                    field.handleChange(e.target.value as StorySlug);
                  }}
                  onBlur={field.handleBlur}
                >
                  {Object.values(StorySlug).map((slug) => (
                    <MenuItem key={slug} value={slug}>
                      {prettyStoryType(slug)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <form.Field
              name="storyConfigRevisionNumber"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) {
                    return "Story config revision is required";
                  }
                },
              }}
              children={(field) => (
                <TextField
                  label="Story config revision"
                  value={field.state.value ?? ""}
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  select={true}
                  onChange={(e) => {
                    field.handleChange(parseInt(e.target.value));
                  }}
                  onBlur={field.handleBlur}
                  disabled={!field.form.getFieldValue("storyType")}
                >
                  {storyConfigRevisionOptions.map((revision) => (
                    <MenuItem key={revision} value={revision}>
                      {revision}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Stack direction="row" gap={2}>
              <form.Field
                name="promptName"
                validators={{
                  onSubmit: ({ value }) => {
                    if (!value) {
                      return "Prompt name is required";
                    }
                  },
                }}
                listeners={{
                  onChange: ({ fieldApi }) => {
                    fieldApi.form.resetField("promptRevisionNumber");
                  },
                }}
                children={(field) => (
                  <TextField
                    label="Prompt name"
                    value={field.state.value ?? ""}
                    error={!field.state.meta.isValid}
                    helperText={field.state.meta.errors[0]}
                    select={true}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    fullWidth
                    disabled={!field.form.getFieldValue("storyType")}
                  >
                    {promptNameOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <form.Field
                name="promptRevisionNumber"
                validators={{
                  onSubmit: ({ value }) => {
                    if (!value) {
                      return "Prompt revision is required";
                    }
                  },
                }}
                children={(field) => (
                  <TextField
                    label="Prompt revision"
                    value={field.state.value ?? ""}
                    error={!field.state.meta.isValid}
                    helperText={field.state.meta.errors[0]}
                    select={true}
                    onChange={(e) => {
                      field.handleChange(parseInt(e.target.value));
                    }}
                    onBlur={field.handleBlur}
                    fullWidth
                    disabled={!field.form.getFieldValue("promptName")}
                  >
                    {promptRevisionOptions.map((revision) => (
                      <MenuItem key={revision} value={revision}>
                        {revision}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
            <Stack direction="row" gap={2}>
              <form.Field
                name="imagePromptName"
                listeners={{
                  onChange: ({ fieldApi }) => {
                    fieldApi.form.resetField("imagePromptRevisionNumber");
                  },
                }}
                children={(field) => (
                  <TextField
                    label="Image prompt name"
                    value={field.state.value ?? ""}
                    error={!field.state.meta.isValid}
                    helperText={field.state.meta.errors[0]}
                    select={true}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    fullWidth
                    disabled={!field.form.getFieldValue("storyType")}
                  >
                    {imagePromptNameOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <form.Field
                name="imagePromptRevisionNumber"
                validators={{
                  onSubmit: ({ value, fieldApi }) => {
                    if (
                      fieldApi.form.getFieldValue("imagePromptName") &&
                      !value
                    ) {
                      return "Image prompt revision is required";
                    }
                  },
                }}
                children={(field) => (
                  <TextField
                    label="Image prompt revision"
                    value={field.state.value ?? ""}
                    error={!field.state.meta.isValid}
                    helperText={field.state.meta.errors[0]}
                    select={true}
                    onChange={(e) => {
                      field.handleChange(parseInt(e.target.value));
                    }}
                    onBlur={field.handleBlur}
                    fullWidth
                    disabled={!field.form.getFieldValue("imagePromptName")}
                  >
                    {imagePromptRevisionOptions.map((revision) => (
                      <MenuItem key={revision} value={revision}>
                        {revision}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
            <form.Field
              name="llmModel"
              children={(field) => (
                <TextField
                  label="LLM model"
                  value={field.state.value ?? ""}
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  select={true}
                  onChange={(e) => {
                    field.handleChange(e.target.value as ModelOption);
                  }}
                  onBlur={field.handleBlur}
                >
                  {Object.values(ModelOption).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={form.handleSubmit}
            loading={isPending}
            variant={"contained"}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

type FormValues = {
  name: string;
  storyType: StorySlug | undefined; // not needed in create endpoint, but needed to show prompt and configs in the form
  storyConfigRevisionNumber: number | undefined;
  promptName: string | undefined;
  promptRevisionNumber: number | undefined;
  imagePromptName: string | undefined;
  imagePromptRevisionNumber: number | undefined;
  llmModel: ModelOption | undefined;
};

// assumes that form values are validated
function fromFormValues(
  values: FormValues,
  storyConfigs: StoryConfig[],
  prompts: Prompt[],
): CreateMasterCombination {
  return {
    name: values.name!,
    storyConfigRevisionId: storyConfigs
      .find((config) => config.storyType === values.storyType)
      ?.revisions.find(
        (rev) => rev.revision === values.storyConfigRevisionNumber,
      )?.id!,
    promptRevisionId: prompts
      .find(
        (prompt) =>
          prompt.name === values.promptName &&
          prompt.storyType === values.storyType,
      )
      ?.revisions.find((rev) => rev.revision === values.promptRevisionNumber)
      ?.id!,
    imagePromptRevisionId: prompts
      .find(
        (prompt) =>
          prompt.name === values.imagePromptName &&
          prompt.storyType === values.storyType,
      )
      ?.revisions.find(
        (rev) => rev.revision === values.imagePromptRevisionNumber,
      )?.id!,
    llmModel: values.llmModel!,
  };
}
